import React, { useMemo } from 'react';
import { Box, Button, Chip, Grid } from '@mui/material';
import { FilterAltOff } from '@mui/icons-material';
import moment from 'moment';
import styles from './styles';
import { getFilterItems, getValueByPath, hasFilterValue } from './utils';

const getEmptyValue = (field) => {
  if (field?.multiple) {
    return [];
  }

  if (
    field?.type === 'select' ||
    field?.type === 'virtual-select' ||
    field?.type === 'date'
  ) {
    return null;
  }

  return '';
};

const getOptionLabel = (field, value) => {
  if (value && typeof value === 'object') {
    return value?.label || value?.value || '';
  }

  const option = field?.options?.find(
    (item) => String(item?.value) === String(value)
  );

  return option?.label || value;
};

const formatValue = (field, value) => {
  if (Array.isArray(value)) {
    return value?.map((item) => getOptionLabel(field, item))?.join(', ');
  }

  if (field?.type === 'select' || field?.type === 'virtual-select') {
    return getOptionLabel(field, value);
  }

  if (field?.type === 'date') {
    return moment(value).format('DD/MM/YYYY');
  }

  return value;
};

const getItemValue = (item, values) => {
  if (item?.type !== 'range') {
    return getValueByPath(values, item.field.name);
  }

  const [initialField, finalField] = item.fields;
  const initialValue = getValueByPath(values, initialField.name);
  const finalValue = getValueByPath(values, finalField.name);

  if (!hasFilterValue(initialValue) && !hasFilterValue(finalValue)) {
    return null;
  }

  return {
    initial: initialValue,
    final: finalValue,
  };
};

const formatItemValue = (item, value) => {
  if (item?.type !== 'range') {
    return formatValue(item.field, value);
  }

  const [initialField, finalField] = item.fields;
  const initial = hasFilterValue(value?.initial)
    ? formatValue(initialField, value.initial)
    : '...';
  const final = hasFilterValue(value?.final)
    ? formatValue(finalField, value.final)
    : '...';

  return `${initial} até ${final}`;
};

const ActiveFilters = ({ fields = [], form, grid, onChange }) => {
  const values = grid?.filter || {};

  const activeFilters = useMemo(
    () =>
      getFilterItems(fields, grid)
        ?.map((item) => ({
          item,
          value: getItemValue(item, values),
        }))
        ?.filter(({ value }) => hasFilterValue(value)),
    [fields, grid, values]
  );

  if (!form || !activeFilters?.length) {
    return null;
  }

  const handleChange = () => {
    if (onChange) {
      onChange();
    }
  };

  const handleDelete = (item) => {
    const itemFields = item?.type === 'range' ? item.fields : [item.field];

    itemFields?.forEach((field) => {
      form.setValue(field.name, getEmptyValue(field));
    });
    handleChange();
  };

  const handleClear = () => {
    activeFilters?.forEach(({ item }) => {
      const itemFields = item?.type === 'range' ? item.fields : [item.field];

      itemFields?.forEach((field) => {
        form.setValue(field.name, getEmptyValue(field));
      });
    });
    handleChange();
  };

  return (
    <Grid container spacing={1.5} sx={styles.activeFiltersContainer}>
      <Grid item xs={12} lg={4} xl={5} sx={styles.activeFiltersSpacer} />
      <Grid item xs={12} lg={8} xl={7}>
        <Box sx={styles.activeFilters}>
          {activeFilters?.map(({ item, value }) => (
            <Chip
              key={item.key}
              size="small"
              variant="outlined"
              label={`${item.label}: ${formatItemValue(item, value)}`}
              onDelete={() => handleDelete(item)}
            />
          ))}
          <Button
            size="small"
            variant="text"
            startIcon={<FilterAltOff />}
            onClick={handleClear}
          >
            Limpar filtros
          </Button>
        </Box>
      </Grid>
    </Grid>
  );
};

export default ActiveFilters;
